import {ChangeDetectionStrategy, Component, computed, inject, Signal, signal} from "@angular/core";
import {navParam} from "../../../../navigation/navigation-params";
import {Page} from "../../../../navigation/page";
import {NotificationService} from "../../../../notification/notification.service";
import {OrderModel} from "../../../order.model";
import {OrderingContextModel} from '../../../../ordering-context/ordering-context.model';
import {MenuItemData, MenuPlgSellableData, MenuSellableData} from '../../../../menu/menu-entity.model';
import {MenuModel} from '../../../../menu/menu.model';
import {OrderCompositeItem, OrderPlgItem} from '../../../order';
import {NgClass} from '@angular/common';
import {ModalPageLayoutComponent} from '../../../../page-layout/modal-page-layout/modal-page-layout.component';
import {OrderItemOptionControlComponent} from "../order-item-option-control/order-item-option-control.component";
import {DietaryPanelComponent} from "../../dietary-panel/dietary-panel.component";
import {ButtonComponent} from '../../../../buttons/button/button.component';
import {PageFooterControlDirective} from '../../../../page-layout/page-footer-control.directive';

@Component({
    selector: "woa-order-item-plg-edit-page",
    templateUrl: "./order-item-plg-edit-page.component.html",
    styleUrl: "./order-item-plg-edit-page.component.scss",
    providers: [{provide: Page, useExisting: OrderItemPlgEditPageComponent}],
    imports: [
        ButtonComponent,
        DietaryPanelComponent,
        ModalPageLayoutComponent,
        NgClass,
        OrderItemOptionControlComponent,
        PageFooterControlDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemPlgEditPageComponent extends Page {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private orderModel: OrderModel = inject(OrderModel);
    public menuModel: MenuModel = inject(MenuModel);
    private notificationService: NotificationService = inject(NotificationService);

    public readonly orderItem: Signal<OrderCompositeItem> = navParam<OrderCompositeItem>("orderItem");
    public readonly currentItem: Signal<OrderPlgItem> = navParam<OrderPlgItem>("currentItem");
    public readonly menuItem: Signal<MenuItemData> = navParam<MenuItemData>("menuItem");

    private readonly originalSelectedSellableId: Signal<number> = signal(this.currentItem()?.sellable()?.sellableId);
    public readonly plgItem: Signal<OrderPlgItem> = signal(new OrderPlgItem(undefined, this.currentItem().plg, this.currentItem().nameSignal(), this.currentItem().sellable(), this.currentItem().quantitySignal()));

    public readonly availablePlgSellables: Signal<MenuPlgSellableData[]> = computed(() =>
        this.plgItem().plg.sellableLines.filter((plgSellable) => this.menuModel.plgItemIsAvailable()(plgSellable.menuItem, this.menuItem(), this.orderItem().selectedFilterTags())));

    public readonly anyHaveImages: Signal<boolean> = computed(() =>
        this.availablePlgSellables().some((plgSellable: MenuPlgSellableData) => !!this.menuModel.standardItemImage()(plgSellable.menuItem)));

    public readonly isPlgSellableSelected: Signal<(plgSellable: MenuPlgSellableData) => boolean> = computed(() => {
        const sellable: MenuSellableData = this.plgItem().sellable();
        return (plgSellable: MenuPlgSellableData): boolean =>
            sellable && plgSellable.sellable && plgSellable.sellable.sellableId === sellable.sellableId;
    });

    private readonly isPlgSellableAtLimit: Signal<(plgSellable: MenuPlgSellableData) => boolean> = computed(() => {
        const sellableAtLimit: (sellableId: number, quantum?: number, qtyPending?: number, excludedQty?: number) => boolean = this.orderModel.sellableAtLimit();
        const quantity: number = this.orderItem().quantitySignal();
        const sellableQtyOrdered: (sellableId: number) => number = this.orderItem().sellableQtyOrdered();
        return (plgSellable: MenuPlgSellableData): boolean =>
            plgSellable.sellable.sellableId !== this.originalSelectedSellableId() &&
            sellableAtLimit(plgSellable.menuItem ? plgSellable.menuItem.sellable.sellableId : plgSellable.sellable.sellableId,
                quantity, sellableQtyOrdered(plgSellable.sellable.sellableId));
    });

    public readonly showPlgSellableUnavailable: Signal<(plgSellable: MenuPlgSellableData) => boolean> = computed(() => {
        const isPlgSellableAtLimit: (plgSellable: MenuPlgSellableData) => boolean = this.isPlgSellableAtLimit();
        const isPlgSellableSelected: (plgSellable: MenuPlgSellableData) => boolean = this.isPlgSellableSelected();
        return (plgSellable: MenuPlgSellableData): boolean =>
            isPlgSellableAtLimit(plgSellable) && !isPlgSellableSelected(plgSellable);
    });

    public async selectPlgSellable(plgSellable: MenuPlgSellableData): Promise<void> {
        if (this.isPlgSellableSelected()(plgSellable)) {
            return;
        }

        if (this.isPlgSellableAtLimit()(plgSellable)) {
            if (this.menuModel.itemOutOfStock()(plgSellable.menuItem)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getMenuItemDisplayName(plgSellable.menuItem)});
            } else {
                void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getMenuItemDisplayName(plgSellable.menuItem)});
            }
            return;
        }

        if (this.orderingContextModel.currentStore().rsaMinimumAge && plgSellable.sellable.applyRsa && !await this.orderModel.verifyRsa()) {
            return;
        }

        this.plgItem().selectSellable(plgSellable.sellable, this.orderItem());
    }
}
